/**
 * The Context tab's source list.
 *
 * PURELY PRESENTATIONAL: every row, health state, and callback arrives as a
 * prop. The container (`ContextTab`) owns the SDK and the mutations. That split
 * is what lets the functional tier drive every state — including states the
 * live API cannot yet produce — from real-shaped fixtures rather than from a
 * faked backend.
 */
import { useMemo } from 'react';
import { Badge } from '@localagent/ui/components/badge';
import { Button } from '@localagent/ui/components/button';
import { ListAddButton } from '@localagent/ui/components/list-filter-menu';
import { DataList, PencilIcon, PowerIcon, PowerOffIcon, TrashIcon } from '@localagent/ui/components/data-list';
import type { ContextSource, ContextSourceHealth } from '@localagent/sdk';

/**
 * The health affordance.
 *
 * The three assembler states plus the client-derived never-observed state:
 * - `healthy` renders nothing — a clean list means nothing needs attention.
 * - `omitted` renders a MUTED, non-alarming note. This is the state the whole
 *   three-way split exists for: a `referenced` source with no substrate
 *   resolves to nothing forever, and if it rendered identically to a healthy
 *   source the user would never learn their configured source contributes
 *   nothing. It is not an error — the turn succeeded.
 * - `error` renders a DESTRUCTIVE badge. A transient read failure attributable
 *   to this source, which fails the turn.
 * - `not_yet_observed` renders a muted "Not yet observed" badge — the source
 *   is in sources.list but ABSENT from the account health response.
 *
 * `omitted` and `error` must stay distinguishable in BOTH colour and wording;
 * Healthy stays silent.
 */
export type SourceHealthDisplay = ContextSourceHealth | 'not_yet_observed';

/**
 * Per-source read health, keyed by source id.
 *
 * Health is produced ONLY by the per-turn assembler, which alone distinguishes
 * a structural absence from an op error — the client must never infer it from
 * enabled/data_source. Absence from the account health response is derived as
 * `not_yet_observed` by the container before it reaches this map.
 */
export type SourceHealthMap = Record<string, SourceHealthDisplay>;

export interface ContextSourceListProps {
  sources: ContextSource[];
  /** Assembler-reported / derived health. Omit entirely when no read has completed. */
  health?: SourceHealthMap;
  isLoading?: boolean;
  /**
   * True when the source-list read FAILED. A failed read is not an empty list —
   * it renders an explicit error affordance rather than the "nothing configured"
   * empty state, which would otherwise be indistinguishable from success.
   */
  isError?: boolean;
  /** The read error, surfaced when `isError`. */
  error?: Error | null;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onCreate: () => void;
  onEdit: (source: ContextSource) => void;
  onDelete: (source: ContextSource) => void;
  onToggle: (source: ContextSource, enabled: boolean) => void;
  /** Move a row to an absolute 0-based position within its scope bucket. */
  onMove: (source: ContextSource, position: number) => void;
  /** Row-level error text surfaced by the container's mutation wrapper. */
  rowError?: string | null;
  busy?: boolean;
}

/**
 * The two v1 placements, and the two deferred to v2 (context
 * ADR-005). The deferred pair is rendered as a known-but-unavailable
 * affordance rather than omitted, so a user reading the tab learns the axis
 * exists and is coming — never selects one and eats a 422.
 */
const DEFERRED_PLACEMENTS = new Set(['message-history', 'step-scoped']);

function HealthAffordance({
  health,
  sourceName,
  skipHealth,
}: {
  health: SourceHealthDisplay | undefined;
  sourceName: string;
  /** Platform-reserved Built-in row carries no health affordance. */
  skipHealth?: boolean;
}) {
  if (skipHealth || health === undefined || health === 'healthy') return null;

  if (health === 'not_yet_observed') {
    return (
      <Badge
        variant="outline"
        className="text-muted-foreground"
        data-testid={`context-source-health-not-yet-observed-${sourceName}`}
        data-health="not_yet_observed"
        title="This source has not ridden a turn yet — no health has been observed."
      >
        Not yet observed
      </Badge>
    );
  }

  if (health === 'omitted') {
    return (
      <Badge
        variant="outline"
        className="text-muted-foreground"
        data-testid={`context-source-health-omitted-${sourceName}`}
        data-health="omitted"
        title="This source resolved to nothing on the last turn — no workspace, no connected device, or no content at its reference. The turn still succeeded."
      >
        Contributed nothing
      </Badge>
    );
  }

  return (
    <Badge
      variant="destructive"
      data-testid={`context-source-health-error-${sourceName}`}
      data-health="error"
      title="This source failed to read on the last turn and stopped the turn. Check its reference or content."
    >
      Read failed
    </Badge>
  );
}

export function ContextSourceList({
  sources,
  health,
  isLoading,
  isError,
  error,
  searchValue,
  onSearchChange,
  onCreate,
  onEdit,
  onDelete,
  onToggle,
  onMove,
  rowError,
  busy,
}: ContextSourceListProps) {
  const visible = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return sources;
    return sources.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.type.toLowerCase().includes(q) ||
        s.scope.toLowerCase().includes(q),
    );
  }, [sources, searchValue]);

  /**
   * Reorder operates on the USER BAND of a scope bucket, and
   * `ordering` is dense per bucket — so a row's neighbours are the rows sharing
   * its scope, not its neighbours in the flat list. Bucket sizes are computed
   * here so the ↑/↓ buttons disable at the true band edges rather than at the
   * edges of the rendered list.
   */
  const bucketSizes = useMemo(() => {
    const sizes = new Map<string, number>();
    for (const s of sources) {
      // Locked structural descriptors (environment / user-info / steering) are
      // synthesized for display and are NOT reorderable user-band rows, so they
      // must not count toward a bucket's size — otherwise the ↓ button on the
      // last real row stays enabled and offers a position the backend rejects.
      if (s.locked) continue;
      sizes.set(s.scope, (sizes.get(s.scope) ?? 0) + 1);
    }
    return sizes;
  }, [sources]);

  return (
    <div className="flex flex-col gap-4" data-testid="context-source-list">
      {rowError && (
        <p className="text-body text-destructive" data-testid="context-source-row-error">
          {rowError}
        </p>
      )}

      <DataList<ContextSource>
        items={visible}
        isLoading={isLoading}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search sources..."
        headerActions={
          <ListAddButton label="New source" testId="new-context-source-button" onClick={onCreate} />
        }
        getItemId={(s) => s.id}
        itemActions={(source) => [
          {
            label: source.enabled ? 'Disable' : 'Enable',
            icon: source.enabled ? <PowerOffIcon /> : <PowerIcon />,
            testId: `context-source-toggle-${source.name}`,
            disabled: busy,
            onClick: () => onToggle(source, !source.enabled),
          },
          ...(source.locked
            ? []
            : [
                {
                  label: 'Edit',
                  icon: <PencilIcon />,
                  testId: `context-source-edit-${source.name}`,
                  disabled: busy,
                  onClick: () => onEdit(source),
                },
                {
                  label: 'Delete',
                  icon: <TrashIcon />,
                  variant: 'destructive' as const,
                  testId: `context-source-delete-${source.name}`,
                  disabled: busy,
                  onClick: () => onDelete(source),
                },
              ]),
        ]}
        emptyState={
          isError ? (
            <div
              className="rounded-md bg-destructive/10 p-3 text-body text-destructive"
              data-testid="context-sources-error"
            >
              {error?.message ?? 'Failed to load context sources.'}
            </div>
          ) : (
            <div
              className="py-10 text-center text-body text-muted-foreground"
              data-testid="context-sources-empty"
            >
              No context sources yet. Add one to give every agent standing
              instructions, knowledge, or memory.
            </div>
          )
        }
        renderItem={(source) => {
          // A locked structural source is never editable or deletable —
          // there is nothing to edit (it is not a stored row). That is now the
          // ONLY read-only class: an account rule
          // is an ordinary row and is fully editable here.
          const readOnly = source.locked;
          const bucketSize = bucketSizes.get(source.scope) ?? 0;

          return (
            <div
              className="flex items-center gap-3 py-2"
              data-testid={`context-source-row-${source.name}`}
              data-scope={source.scope}
              data-type={source.type}
              data-placement={source.placement}
              data-ordering={source.ordering}
              data-enabled={source.enabled}
              data-locked={source.locked}
            >
              <div className="flex shrink-0 flex-col">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Move ${source.name} up`}
                  data-testid={`context-source-move-up-${source.name}`}
                  disabled={readOnly || source.ordering === 0 || busy}
                  onClick={() => onMove(source, source.ordering - 1)}
                >
                  ↑
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Move ${source.name} down`}
                  data-testid={`context-source-move-down-${source.name}`}
                  disabled={readOnly || source.ordering >= bucketSize - 1 || busy}
                  onClick={() => onMove(source, source.ordering + 1)}
                >
                  ↓
                </Button>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-medium">{source.name}</span>
                  {source.locked && (
                    <Badge
                      variant="secondary"
                      data-testid={`context-source-locked-${source.name}`}
                      title="A built-in structural source. It always applies and cannot be edited or removed."
                    >
                      Built-in
                    </Badge>
                  )}
                  <HealthAffordance
                    health={health?.[source.id]}
                    sourceName={source.name}
                    skipHealth={source.id === 'platform-reserved'}
                  />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-caption text-muted-foreground">
                  <span data-testid={`context-source-type-${source.name}`}>
                    {source.type}
                  </span>
                  <span>·</span>
                  <span>{source.data_source}</span>
                  <span>·</span>
                  <span data-testid={`context-source-placement-${source.name}`}>
                    {DEFERRED_PLACEMENTS.has(source.placement)
                      ? `${source.placement} (coming in v2)`
                      : source.placement}
                  </span>
                  <span>·</span>
                  <span data-testid={`context-source-scope-${source.name}`}>
                    {source.scope}
                  </span>
                  {source.reference_path && (
                    <>
                      <span>·</span>
                      <span className="truncate">{source.reference_path}</span>
                    </>
                  )}
                </div>
              </div>

            </div>
          );
        }}
      />
    </div>
  );
}
